import { z } from 'zod';
import { ChannelId } from './common.js';

/**
 * THREE ORTHOGONAL AXES, never one enum.
 *
 * L51 — conflating them is what made "a wrapped mint counts as collected"
 * possible, and the consequence there was a whole season of cards with no
 * collected record. An item can be active, wrapped and locked in a trade all
 * at once; a single status field cannot say that, so it says something else
 * and somebody writes code believing it.
 */
export const InstanceStatus = z.enum(['active', 'void']);
export const InstanceReveal = z.enum(['wrapped', 'revealed']);
export const InstanceCustody = z.enum(['owner', 'locked']);

/**
 * A minted copy. The only source of ownership truth (L49).
 *
 * `users/{uid}/inventory` is a PROJECTION of this — rebuildable, never read by
 * business logic. Their platform had three sources of ownership truth and
 * most of its ~155 repair scripts existed to reconcile them.
 */
export const Instance = z.object({
  id: z.string(),
  channel_id: ChannelId,
  owner_uid: z.string(),
  status: InstanceStatus,
  reveal: InstanceReveal,
  custody: InstanceCustody,
  reward_id: z.string(),
  bundle_id: z.string().nullable(),
  /**
   * WHICH collectible this is, and at what tier — both stamped at mint.
   *
   * The tier lives here and nowhere else. stefancoat has no rarity field on
   * the copy at all for audio: it is recovered by SUBSTRING-MATCHING the
   * Firestore document id (`itemInfo.ts:42-50`), because the stored variant
   * drifted from the assigned tier and the id was the only thing left that
   * still agreed. goodgamegizmo gives two contradictory answers in one repo
   * about whether rarity belongs to the asset or the copy.
   *
   * It is the KEY, never the label, so renaming "Epic" to "Mythic" carries
   * every copy already minted (L54). `null` means the reward has no tiers,
   * which is not the same as a tier that was forgotten.
   */
  item_id: z.string().nullable(),
  tier: z.string().nullable(),
  /**
   * `number` and `of` are null together for an uncapped run.
   *
   * Stamped at mint and never rewritten: "#3 of 50" is a receipt of what was
   * promised, so the print run cannot move underneath it (L67).
   */
  edition: z.object({
    number: z.number().int().min(1).nullable(),
    of: z.number().int().min(1).nullable(),
  }),
  provenance: z.object({
    minted_at: z.string().datetime(),
    source_type: z.string(),
    original_owner_uid: z.string(),
    transfer_count: z.number().int().min(0),
  }),
  /** Null while wrapped. */
  revealed_at: z.string().datetime().nullable(),
});

/**
 * One row per (channel, person, reward) they have EVER owned.
 *
 * L51 — written at REVEAL, not at mint, and never deleted. Their checklist
 * read from current inventory, so trading a card away made it forget you had
 * ever owned it. This is the record that remembers.
 */
export const Collected = z.object({
  channel_id: ChannelId,
  reward_id: z.string(),
  first_collected_at: z.string().datetime(),
});

export type Instance = z.infer<typeof Instance>;
export type Collected = z.infer<typeof Collected>;
